import { ObjectSchema, array, object, string } from 'yup';
import { noop } from '@utils/noop';
import { Dispatch, createContext, useContext } from 'react';
import { UseFormReturn } from 'react-hook-form';

export type CreateAviatorUserFormState = {
  email: string;
  password: string;
  providerId: { providerId: string; rgsId: string } | null;
  roleIds: string[] | string;
  games: number[];
};

export const createAviatorUserFormStateDefault: CreateAviatorUserFormState = {
  email: '',
  password: '',
  providerId: null,
  roleIds: '',
  games: [],
};

export const createAviatorUserSchemaDefault: ObjectSchema<Partial<CreateAviatorUserFormState>> =
  object<Partial<CreateAviatorUserFormState>>().shape({
    email: string().email().required('email is required field'),
    password: string()
      .required('Password is required field')
      .test('no-whitespace', 'Password cannot contain spaces', function (value) {
        return !value || !/\s/.test(value);
      })
      .min(8, 'Password must be at least 8 characters')
      .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
      .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
      .matches(/[0-9]/, 'Password must contain at least one number')
      .matches(/[^a-zA-Z0-9]/, 'Password must contain at least one symbol')
      .test('password-not-contain-email', 'Password cannot contain the email', function (value) {
        const email = this.parent.email;
        if (!email || !value) return true;
        const trimmedPassword = value.replace(/\s/g, '').toLowerCase();
        const trimmedEmail = email.replace(/\s/g, '').toLowerCase();
        return !trimmedPassword.includes(trimmedEmail);
      }),
    providerId: object<{ providerId: string; rgsId: string }>()
      .shape({
        providerId: string().required('providerId is required field'),
        rgsId: string().required('rgsId is required field'),
      })
      .optional()
      .nullable(),
    roleIds: string().required('roleIds is required field'),
    games: array()
      .min(1, 'games array must have at least one game')
      .required('games is required field'),
  });

type CreateAviatorUserFormContextType = {
  schema: ObjectSchema<Partial<CreateAviatorUserFormState>>;
  setSchema: Dispatch<ObjectSchema<Partial<CreateAviatorUserFormState>>>;
  methods?: UseFormReturn<CreateAviatorUserFormState>;
};

const createAviatorUserFormContextDefault = {
  schema: createAviatorUserSchemaDefault,
  setSchema: noop,
} satisfies CreateAviatorUserFormContextType;

export const CreateAviatorUserFormContext = createContext<CreateAviatorUserFormContextType>(
  createAviatorUserFormContextDefault
);

export const useCreateAviatorUserForm = () => {
  const { methods } = useContext(CreateAviatorUserFormContext);
  if (!methods) {
    throw new Error('CreateAviatorUserFormContext is empty');
  }

  return methods;
};

export const useManageCreateAviatorUserSchema = () => {
  const { setSchema } = useContext(CreateAviatorUserFormContext);

  return { setSchema };
};
